import { SmButton, SmCalendar, SmIcon } from '@smiles/smiles-react-design-system';
import React, { useEffect, useRef, useState } from 'react';
import { HeaderMobile } from '..';
import { useContextSelector } from '../../hooks';
import { disableDatesByAntecedence, formatCalendarValues } from './utils';

import type {
  TCalendarFunction,
  TCalendarValue,
} from '@smiles/smiles-react-design-system/dist/components/V2/Calendar/types';
import { ERROR_MESSAGES } from '../../constants';
import { TabTypes, type ItineraryData } from '../../types';

export interface ICalendarProps {
  id: string;
  isOpen: boolean;
  onSingleDateClick?: (dates: TCalendarValue) => void;
  onDatesSelect: (dates: ItineraryData) => void;
  onCalendarClose: () => void;
}

export const Calendar = ({
  id,
  isOpen,
  onCalendarClose,
  onDatesSelect,
  onSingleDateClick,
  ...props
}: ICalendarProps) => {
  const {
    search: { dates, location },
    generics: { isMobile },
    liferay: { antecedenceTime },
  } = useContextSelector();
  const [previewDates, setPreviewDates] = useState<ItineraryData>({ pickup: null, dropoff: null });
  const [activeTab, setActiveTab] = useState<TabTypes>('pickup');
  const [calendarValues, setCalendarValues] = useState<TCalendarValue>({});
  const [hasSameDate, setHasSameDate] = useState<boolean>(false);
  const calendarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isMobile) {
      const handleClickOutside = ({ target }: Event) => {
        const calendarPickupInput = document.querySelector('#calendar-input-field-pickup') as HTMLInputElement;
        const calendarDropoffInput = document.querySelector('#calendar-input-field-dropoff') as HTMLInputElement;
        const isAnyInputFocused = [calendarPickupInput, calendarDropoffInput].some((input) =>
          input.classList.contains('focused'),
        );

        if (calendarRef.current && !calendarRef.current.contains(target as Node) && !isAnyInputFocused) {
          onCalendarClose();
        }
      };

      document.addEventListener('click', handleClickOutside, true);

      return () => {
        document.removeEventListener('click', handleClickOutside, true);
      };
    }
  }, []);

  // useEffect to persist calendar previous values on Desktop version
  useEffect(() => {
    if (!isMobile && !isOpen) {
      const datesOutput = formatCalendarValues(dates);
      setCalendarValues(datesOutput);
      setPreviewDates(dates);
    }
  }, [isOpen, isMobile]);

  // useEffect to control the focused input according to the date selection (Desktop version visual feedback only)
  useEffect(() => {
    const calendarPickupInput = document.querySelector('#calendar-input-field-pickup') as HTMLInputElement;
    const calendarDropoffInput = document.querySelector('#calendar-input-field-dropoff') as HTMLInputElement;

    if (isOpen) {
      if (previewDates.pickup) {
        calendarPickupInput.blur();
        calendarDropoffInput.focus();
      }
    }

    if (previewDates.dropoff) {
      calendarPickupInput.blur();
      calendarDropoffInput.blur();
    }
  }, [previewDates]);

  useEffect(() => {
    const datesOutput = formatCalendarValues(dates);
    setCalendarValues(datesOutput);
  }, [dates]);

  const handleCalendarClose = () => {
    if (!dates.pickup) {
      setPreviewDates({ pickup: null, dropoff: null });
      setCalendarValues({ start: undefined, end: undefined });
    } else {
      setPreviewDates(dates);

      const datesOutput = formatCalendarValues(dates);
      setCalendarValues(datesOutput);
    }

    onCalendarClose();
  };

  const handleDateSelect: TCalendarFunction = (selectedDate) => {
    setHasSameDate(false);

    if (selectedDate) {
      const selectedDatesISO: ItineraryData = {
        pickup: null,
        dropoff: null,
      };

      if (selectedDate.start) {
        selectedDatesISO.pickup = selectedDate.start.toISOString();

        setActiveTab('dropoff');
      }

      if (selectedDate.end) {
        selectedDatesISO.dropoff = selectedDate.end.toISOString();
      } else {
        setActiveTab('pickup');
      }

      setPreviewDates(selectedDatesISO);

      // Desktop date confirm selection
      if (!isMobile) {
        confirmDates(selectedDatesISO);
      }
    }
  };

  const handleMobileConfirm = () => {
    if (previewDates.pickup && previewDates.dropoff) {
      const pickupDate = new Date(previewDates.pickup);
      const dropoffDate = new Date(previewDates.dropoff);

      if (dropoffDate <= pickupDate) {
        setHasSameDate(true);
      } else {
        confirmDates(previewDates);
      }
    }
  };

  const confirmDates = (dates: ItineraryData) => {
    onDatesSelect(dates);

    // Will only close the calendar when both dates are selected
    if (dates.pickup && dates.dropoff) {
      onCalendarClose();
    }
  };

  return (
    <section
      className={`calendar-datepicker-container${isOpen ? ' open' : ''}`}
      id={`calendar-container-${id}`}
      ref={calendarRef}
      {...props}
    >
      {isMobile && (
        <HeaderMobile.Tabbed
          type="dates"
          label="Período de locação"
          activeTab={activeTab}
          selectedData={previewDates}
          onIconClick={handleCalendarClose}
        />
      )}

      <section className={`calendar-datepicker-wrapper${previewDates.pickup ? ' scroll' : ''}`}>
        <SmCalendar
          multiCalendar
          disablePast
          disableSaturdays={false}
          disableSundays={false}
          disableHolidays={false}
          value={calendarValues}
          onSelectDate={handleDateSelect}
          disabledDates={disableDatesByAntecedence(location, antecedenceTime)} // desabilita datas que nao tem horarios após a aplicação da antecedencia em horas
        />
      </section>

      {isMobile && (
        <section className={`calendar-datepicker-mobile-footer${previewDates.pickup ? ' open' : ''}`}>
          <article
            className={`calendar-datepicker-mobile-footer-disabled-message${!previewDates.dropoff ? ' visible' : ''}${hasSameDate ? ' error' : ''}`}
          >
            <SmIcon
              className="calendar-datepicker-mobile-footer-disabled-message-icon"
              name="info_outlined"
              type="round"
            />
            <span className="calendar-datepicker-mobile-footer-disabled-message-text">
              {hasSameDate ? ERROR_MESSAGES.dates.dropoff.sameDate : 'Selecione a data de devolução'}
            </span>
          </article>

          <SmButton
            className="calendar-datepicker-mobile-footer-button"
            text="Confirmar datas"
            disabled={!(previewDates.pickup && previewDates.dropoff) || hasSameDate}
            onClick={handleMobileConfirm}
          />
        </section>
      )}
    </section>
  );
};
